import { Modal } from '@/components';
import { useStackSiderView } from '@/providers/StackSiderViewProvider';
import { useFormDirtyStore } from '@/stores/form-dirty-store';
import {
  type ContextType,
  type FC,
  type MutableRefObject,
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';

export const LeavePageAlert: FC = () => {
  const { t } = useTranslation();
  const { isDirty } = useFormDirtyStore();
  const {
    state: { stack: siderStack },
  } = useStackSiderView();

  const { leavePageAlertReference } = useLeavePageAlertState();

  const isSiderOpen = siderStack.length > 0;

  /**
   * ナビゲーションがブロック対象かどうかを判定する関数
   * - フォームがダーティでない場合はブロックしない
   * - pathname が同一の場合 (クエリパラメータのみの変更) はブロックしない
   *   これにより、事業詳細ページ内のタブ変更では離脱モーダルを表示しない
   * - それ以外 (別ページへの遷移) はブロックする
   */
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }: any) => {
      if (!isDirty) {
        return false;
      }

      const isSamePath = currentLocation.pathname === nextLocation.pathname;
      const isSearchChanged = currentLocation.search !== nextLocation.search;

      // タブ切替 (search のみ変化) ＋ Sider が開いている場合 → ブロックしない
      if (isSamePath && isSearchChanged && isSiderOpen) {
        return false;
      }

      // タブ切替 (search のみ変化) かつ Drawer が開いている/開いていない → ブロックする
      if (isSamePath && isSearchChanged) {
        return true;
      }

      // その他のページ遷移 or ダミー遷移 → ブロックする
      return true;
    },
    [isDirty, isSiderOpen],
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    const handleUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [isDirty]);

  return (
    <Modal
      title={t('title-confirm-leave-page')}
      open={blocker.state === 'blocked'}
      onOk={() => {
        blocker.proceed?.();
        if (leavePageAlertReference.current.afterAllowAction) {
          leavePageAlertReference.current.afterAllowAction();
          leavePageAlertReference.current.afterAllowAction = undefined;
        }
      }}
      onCancel={() => {
        blocker.reset?.();
        if (leavePageAlertReference.current.afterCancelAction) {
          leavePageAlertReference.current.afterCancelAction();
          leavePageAlertReference.current.afterCancelAction = undefined;
        }
      }}
      closable={false}
      maskClosable={false}
    >
      {t('message-confirm-leave-page')}
    </Modal>
  );
};

type LeavePageAlertState = {
  afterAllowAction?: () => void;
  afterCancelAction?: () => void;
};
type LeavePageAlertContextType = {
  leavePageAlertReference: MutableRefObject<LeavePageAlertState>;
};
const Context = createContext<LeavePageAlertContextType | undefined>(undefined);

export const useLeavePageAlertState = (): NonNullable<
  ContextType<typeof Context>
> => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      'useLeavePageAlertState must be used within LeavePageAlertProvider.',
    );
  }
  return context;
};

export const LeavePageAlertProvider: FC<PropsWithChildren> = ({ children }) => {
  const leavePageAlertReference = useRef<LeavePageAlertState>({});
  return (
    <Context.Provider value={{ leavePageAlertReference }}>
      {children}
    </Context.Provider>
  );
};
